import {
  Button,
  CrudMode,
  UpdateIconButton,
  formatCurrency,
  formatDate,
  formatMinutes,
  ListContainer,
  ListContext,
  LockedIcon,
  PAGE_DEFAULT,
  PAGE_SIZE_UNLIMITED,
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  getSpacing,
} from "@compass/core";
import { ListBeProperty } from "components";
import {
  ContributorMatchListDto,
  ContributorMatchListFilter,
  ContributorMatchListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useList, useRouter } from "hooks";
import produce from "immer";
import "moment-timezone";
import { contributorMatchPageRoute } from "pages/match-main/contributor-matches/ContributorMatchPage/ContributorMatchPage";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { MatchFilter } from "./MatchFilter";

export const Matches = (props: { initialLoad: boolean; contributorId: number; onGetList: () => void }) => {
  const ROOT_PERMISSION: Permission = Permission.ContributorMatch;
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();

  const [listFilter, setListFilter] = useState<ContributorMatchListFilter>({
    matchFilter: { contributorId: props.contributorId },
    page: PAGE_DEFAULT,
    pageSize: PAGE_SIZE_UNLIMITED,
    sortObjects: [{ property: pn<ContributorMatchListDto>("date"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<ContributorMatchListDto[]>([]);
  const [resource, setResource] = useState<ContributorMatchListResource>({});
  const properties = resource.properties || [];

  const { tableContainerRef, loadList, applySort } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.contributorMatches.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
  });

  useEffect(() => {
    if (props.initialLoad) {
      loadList(listFilter);

      ApiService.contributorMatches.getListResource().then((response) => {
        setResource(response);
      });
    }
  }, [props.initialLoad]);

  const goToMatchPage = (matchId: number, crudMode: CrudMode) => {
    navigate({
      pathname: contributorMatchPageRoute.getPath({ id: matchId, crudMode: crudMode, screenType: "dialog" }),
      search: contributorMatchPageRoute.getOptionalParams?.({
        contributorId: props.contributorId,
        hideContributor: true,
      }),
    });
  };

  return (
    <>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent="space-between" spacing="lg">
            <Stack direction="row" alignItems="center" spacing="sm" flexGrow={1}>
              <MatchFilter
                filter={listFilter.matchFilter || {}}
                setFilter={(filter) => {
                  const newListFilter = produce(listFilter, (draft) => {
                    draft.matchFilter = filter;
                  });
                  setListFilter(newListFilter);
                  loadList(newListFilter);
                }}
              />
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button onClick={() => goToMatchPage(0, "edit")} variant="outlined">
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
        tableContainer={
          <TableContainer ref={tableContainerRef}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("date"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("service"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell>
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorMatchListDto>("durationInMinutes")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("amount"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorMatchListDto>("benefitAmount")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ListBeProperty
                      listProperty={properties.find(
                        (x) => x.propertyName === pn<ContributorMatchListDto>("medicalAmount")
                      )}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ListBeProperty
                      listProperty={properties.find((x) => x.propertyName === pn<ContributorMatchListDto>("files"))}
                      sortObjects={listFilter.sortObjects}
                      onChange={(sortObjects) => applySort(sortObjects)}
                    />
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listItems?.map((listItem) => (
                  <TableRow key={listItem.id} onClick={() => goToMatchPage(listItem.id, "view")}>
                    <TableCell>{formatDate(listItem.date)}</TableCell>
                    <TableCell>{listItem.service}</TableCell>
                    <TableCell>{formatMinutes(listItem.durationInMinutes)}</TableCell>
                    <TableCell align="right">{formatCurrency(listItem.amount)}</TableCell>
                    <TableCell align="right">{formatCurrency(listItem.benefitAmount)}</TableCell>
                    <TableCell align="right">{formatCurrency(listItem.medicalAmount)}</TableCell>
                    <TableCell align="center">{listItem.files}</TableCell>
                    <TableCell type="action">
                      {listItem.isLocked === true && <LockedIcon />}

                      {isAuthorized(ROOT_PERMISSION, PermissionAction.Update) &&
                        (!listItem.isLocked || isAuthorized(ROOT_PERMISSION, PermissionAction.Lock)) &&
                        !listItem.isBeforeMatchStaticEndDate && (
                          <UpdateIconButton onClick={() => goToMatchPage(listItem.id, "edit")} />
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      />

      <ListContext.Provider
        value={{
          loadList: (options) => {
            loadList(listFilter, { resetPage: options?.resetPage });
            props.onGetList();
          },
        }}
      >
        {/* Outlet will only render for the designated route to prevent conflicts with others on parent Page */}
        {matchTemplatePath(contributorMatchPageRoute.templatePath) && <Outlet />}
      </ListContext.Provider>
    </>
  );
};
